import { Flex, Input, Text } from '@chakra-ui/react'
import React, { KeyboardEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  allHaveValue,
  hasNoValueOrIsWhitespace,
  hasValue,
  isNumeric,
  noneHaveValue,
  someHaveValue,
} from '~/utils/common'
import { clamp, isBetween, isInRange } from '~/utils/math'
import { tryGetLocaleNumericString } from '~/utils/numberUtils'

import { TimePeriod, TimeUnit } from '@mangovoice/core'
import { TimeMeridiem } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const HOURS_MIN_VALUE = 1
const HOURS_MAX_VALUE = 12
const HOURS_AUTO_FILL_MIN_VALUE = 2
const HOURS_AUTO_FILL_MAX_VALUE = 9
const MAX_TIME_UNIT_CHARS = 2
const MINUTES_AUTO_FILL_MIN_VALUE = 6
const MINUTES_AUTO_FILL_MAX_VALUE = 9
const MAX_MINUTES = 59

interface TimeInputProps {
  /** Names the fields, e.g. "After" gives "After hour", "After minute". */
  label: string
  timePeriod?: TimePeriod
  disabled?: boolean
  mountsFocused?: boolean
  onTimeSet?: (timePeriod?: TimePeriod) => void
}

const getExternalStringValuesOrDefault = (timePeriod?: TimePeriod) => {
  const hours = tryGetLocaleNumericString(timePeriod?.hours) ?? ''
  const minutes = tryGetLocaleNumericString(timePeriod?.minutes) ?? ''
  const meridiem = (timePeriod?.meridiem ?? '') as '' | TimeMeridiem

  return {
    hours,
    minutes,
    meridiem,
  }
}

export const TimeInput = ({
  label,
  timePeriod,
  disabled,
  mountsFocused,
  onTimeSet,
}: TimeInputProps) => {
  const { t } = useTranslation()
  const {
    hours: externalHours,
    minutes: externalMinutes,
    meridiem: externalMeridiem,
  } = getExternalStringValuesOrDefault(timePeriod)

  const [hours, setHours] = React.useState<string>(externalHours)
  const [minutes, setMinutes] = React.useState<string>(externalMinutes)
  const [meridiem, setMeridiem] = React.useState<
    '' | TimeMeridiem.AM | TimeMeridiem.PM
  >(externalMeridiem)
  const [elementNeedsSelection, setElementNeedsSelection] =
    React.useState<HTMLInputElement>()

  const hoursInputRef = React.useRef<HTMLInputElement>(null)
  const minutesInputRef = React.useRef<HTMLInputElement>(null)
  const meridiemInputRef = React.useRef<HTMLInputElement>(null)

  const setTimeValue = (
    timeUnit: TimeUnit,
    timeValue: number | string,
    forceEval = false,
    ignoreFocus = false,
  ) => {
    const valueIsString = typeof timeValue === 'string'
    const stringValue = valueIsString ? timeValue : timeValue.toString()
    let formattedValue = isNumeric(stringValue) ? stringValue : ''

    let numericValue = valueIsString ? parseInt(stringValue) : timeValue

    if (hasValue(formattedValue)) {
      formattedValue = numericValue.toString()

      switch (timeUnit) {
        case TimeUnit.Hours:
          {
            if (
              formattedValue.length < MAX_TIME_UNIT_CHARS &&
              isInRange(
                numericValue,
                HOURS_AUTO_FILL_MIN_VALUE,
                HOURS_AUTO_FILL_MAX_VALUE,
              )
            ) {
              formattedValue = `0${numericValue}`
              forceEval = true
            }

            if (
              (!isBetween(numericValue, HOURS_MIN_VALUE, HOURS_MAX_VALUE) &&
                forceEval) ||
              stringValue.length === MAX_TIME_UNIT_CHARS
            ) {
              //clamp numeric value to 1 or 12
              numericValue = clamp(
                numericValue,
                HOURS_MIN_VALUE,
                HOURS_MAX_VALUE,
              )
              forceEval = true
            }
          }
          break
        case TimeUnit.Minutes:
          {
            if (
              formattedValue.length < MAX_TIME_UNIT_CHARS &&
              isInRange(
                numericValue,
                MINUTES_AUTO_FILL_MIN_VALUE,
                MINUTES_AUTO_FILL_MAX_VALUE,
              )
            ) {
              formattedValue = `0${numericValue}`
              forceEval = true
            }

            numericValue = Math.min(MAX_MINUTES, numericValue)
          }
          break
      }

      if (forceEval) {
        formattedValue = tryGetLocaleNumericString(numericValue)!
      }
    }

    switch (timeUnit) {
      case TimeUnit.Hours:
        {
          if (hasValue(stringValue) && isNaN(numericValue)) {
            setElementNeedsSelection(hoursInputRef.current!)
            return
          }

          if (formattedValue === hours) {
            return
          }

          setHours(formattedValue)
          if (!ignoreFocus && formattedValue.length === 2) {
            minutesInputRef.current?.focus()
          }
        }
        break
      case TimeUnit.Minutes:
        {
          if (hasValue(stringValue) && isNaN(numericValue)) {
            setElementNeedsSelection(minutesInputRef.current!)
            return
          }

          if (formattedValue === minutes) {
            return
          }

          setMinutes(formattedValue)
          if (!ignoreFocus && formattedValue.length === 2) {
            meridiemInputRef.current?.focus()
          }
        }
        break
    }
  }

  const handleSetHours = (e: React.ChangeEvent<HTMLInputElement>) => {
    const textValue = e.target.value
    setTimeValue(TimeUnit.Hours, textValue)
  }

  const handleSetMinutes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const textValue = e.target.value
    setTimeValue(TimeUnit.Minutes, textValue)
  }

  const handleTimeInputBlur = (timeUnit: TimeUnit) => {
    let inputElement: HTMLInputElement
    switch (timeUnit) {
      case TimeUnit.Hours:
        inputElement = hoursInputRef.current!
        break
      case TimeUnit.Minutes:
        inputElement = minutesInputRef.current!
        break
    }

    const inputValue = inputElement!.value ?? ''
    setTimeValue(timeUnit, inputValue, true)
  }

  const handleMeridiemKeyboardInput = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key.toLowerCase()
    switch (key) {
      case 'arrowup':
      case 'arrowdown':
      case ' ':
        e.preventDefault()
        setMeridiem(
          meridiem === TimeMeridiem.AM ? TimeMeridiem.PM : TimeMeridiem.AM,
        )
        break
      case 'home':
        e.preventDefault()
        setMeridiem(TimeMeridiem.AM)
        break
      case 'end':
        e.preventDefault()
        setMeridiem(TimeMeridiem.PM)
        break
      case 'a':
        setMeridiem(TimeMeridiem.AM)
        break
      case 'p':
        setMeridiem(TimeMeridiem.PM)
        break
      case 'backspace':
        setMeridiem(TimeMeridiem.AM)
        break
    }
  }

  const ignoreTypedMeridiem = () => {}

  const handleMeridiemFocused = () => {
    meridiemInputRef.current?.select()
    if (hasNoValueOrIsWhitespace(meridiem)) {
      setMeridiem(TimeMeridiem.AM)
    }
  }

  useEffect(() => {
    if (disabled) {
      return
    }

    if (noneHaveValue([hours, minutes, meridiem])) {
      onTimeSet?.()
    }

    //don't report values have been set if they are not fully set
    const isComplete =
      allHaveValue([hours, minutes, meridiem]) &&
      hours.length === MAX_TIME_UNIT_CHARS &&
      minutes.length === MAX_TIME_UNIT_CHARS

    if (!isComplete) {
      return
    }

    if (allHaveValue([hours, minutes, meridiem]) && onTimeSet) {
      const hoursValue = parseInt(hours)
      const minutesValue = parseInt(minutes)
      const meridiemValue = meridiem as TimeMeridiem

      onTimeSet?.({
        hours: hoursValue,
        minutes: minutesValue,
        meridiem: meridiemValue,
      })
      return
    }
  }, [minutes, hours, meridiem, disabled])

  useEffect(() => {
    if (!elementNeedsSelection) {
      return
    }

    elementNeedsSelection.select()
    setElementNeedsSelection(undefined)
  }, [elementNeedsSelection])

  //handles external value changes
  useEffect(() => {
    if (hasValue(externalHours)) {
      setTimeValue(TimeUnit.Hours, externalHours!, true, true)
    } else {
      setHours('')
    }

    if (hasValue(externalMinutes)) {
      setTimeValue(TimeUnit.Minutes, externalMinutes!, true, true)
    } else {
      setMinutes('')
    }

    if (hasValue(externalMeridiem)) {
      setMeridiem(externalMeridiem!)
    } else {
      setMeridiem('')
    }
  }, [externalHours, externalMinutes, externalMeridiem])

  useEffect(() => {
    if (mountsFocused) {
      hoursInputRef.current?.focus()
    }
  }, [mountsFocused])

  const hasAnyTimeValues = someHaveValue([hours, minutes])
  return (
    <Flex
      position="relative"
      width="full"
      height="2.5625rem"
      borderRadius="0.5rem"
      border="1px solid"
      borderColor="border.default"
      alignItems="center"
      justifyContent="flex-start"
      px="0.5rem"
    >
      <Input
        ref={hoursInputRef}
        value={hours}
        border="none"
        height="90%"
        width="1.75rem"
        mr="-0.125rem"
        p="0rem"
        placeholder={t('form.placeholder.timeHours')}
        aria-label={t('dateTimeRangeModal.hourLabel', '{{label}} hour', {
          label,
        })}
        {...textStyles[textStyleKeys.Body1]}
        textAlign="center"
        cursor="pointer"
        aria-disabled={disabled}
        disabled={disabled}
        _disabled={{
          color: 'border.default',
        }}
        _focus={{
          cursor: 'auto',
        }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
          px: '0.125rem',
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => handleTimeInputBlur(TimeUnit.Hours)}
        onChange={handleSetHours}
      />
      <Text
        {...textStyles[textStyleKeys.Body1]}
        color={hasAnyTimeValues ? 'fg.default' : 'border.default'}
      >
        :
      </Text>
      <Input
        ref={minutesInputRef}
        value={minutes}
        border="none"
        height="90%"
        width="1.75rem"
        p="0rem"
        {...textStyles[textStyleKeys.Body1]}
        placeholder={t('form.placeholder.timeMinutes')}
        aria-label={t('dateTimeRangeModal.minuteLabel', '{{label}} minute', {
          label,
        })}
        textAlign="center"
        cursor="pointer"
        mr="-0.125rem"
        aria-disabled={disabled}
        disabled={disabled}
        _disabled={{
          color: 'border.default',
        }}
        _focus={{
          cursor: 'auto',
        }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => handleTimeInputBlur(TimeUnit.Minutes)}
        onChange={handleSetMinutes}
      />
      <Input
        ref={meridiemInputRef}
        value={meridiem}
        border="none"
        height="90%"
        p="0rem"
        {...textStyles[textStyleKeys.Body1]}
        width="1.75rem"
        ml="0.125rem"
        placeholder={t('form.placeholder.timeMeridiem')}
        role="spinbutton"
        aria-label={t('dateTimeRangeModal.meridiemLabel', '{{label}} AM/PM', {
          label,
        })}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={
          meridiem ? (meridiem === TimeMeridiem.PM ? 1 : 0) : undefined
        }
        aria-valuetext={meridiem || undefined}
        inputMode="none"
        textAlign="center"
        cursor="pointer"
        aria-disabled={disabled}
        disabled={disabled}
        _disabled={{
          color: 'border.default',
        }}
        _focus={{
          cursor: 'auto',
        }}
        _placeholder={{
          ...textStyles[textStyleKeys.Body2],
          color: 'border.default',
          px: '0.125rem',
        }}
        onChange={ignoreTypedMeridiem}
        onSelect={handleMeridiemFocused}
        onFocus={handleMeridiemFocused}
        onKeyDown={handleMeridiemKeyboardInput}
        css={{
          'caret-color': 'transparent !important',
        }}
      />
    </Flex>
  )
}
